import React, { useState } from 'react';
import { MapPin, Phone, Clock, CheckCircle, MessageSquare, Mail } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';
import { useScrollReveal } from '../hooks/useScrollReveal';
import emailjs from '@emailjs/browser';

// EmailJS configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'contact@exevori.com';

export default function ContactPage() {
  const { lang } = useLang();
  const tr = t[lang];
  const cTr = tr.contactPage;
  const [ref, visible] = useScrollReveal();
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', project_type: '', budget: '', message: ''
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      // Format du message complet
      const fullMessage = `
=== EXPÉDITEUR ===
Nom: ${form.name}
Entreprise: ${form.company || 'Non spécifié'}
Email: ${form.email}
Téléphone: ${form.phone || 'Non spécifié'}

=== PROJET ===
Type de projet: ${form.project_type || 'Non spécifié'}
Budget: ${form.budget || 'Non spécifié'}

=== MESSAGE ===
${form.message}
      `.trim();

      // Paramètres EmailJS avec les variables exactes du template
      const templateParams = {
        title: `[Contact] Nouveau message de ${form.name}`,
        name: form.name,
        email: form.email,
        time: new Date().toLocaleString('fr-CA'),
        message: fullMessage
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      console.log('EmailJS Success:', response);
      setStatus('success');
      setForm({ name: '', company: '', email: '', phone: '', project_type: '', budget: '', message: '' });
    } catch (error) {
      console.error('Erreur EmailJS:', error);
      setStatus('error');
    }
  };

  return (
    <main data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-16 relative" style={{ backgroundColor: '#0D1F1F' }}>
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(79,142,247,0.4) 0%, transparent 50%)' }}
        />
        <div className="section-container relative text-center max-w-2xl mx-auto">
          <h1 className="font-sora font-black text-4xl sm:text-5xl text-white mb-3 animate-fadeInUp">
            {cTr.hero.title}
          </h1>
          <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto mb-6" />
          <p className="text-gray-300 text-base md:text-lg font-inter animate-fadeInUp delay-100">
            {cTr.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div ref={ref} className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className={`lg:col-span-3 reveal-left ${visible ? 'visible' : ''}`}>
              <div className="exevori-card p-8 md:p-10">
                {status === 'success' ? (
                  <div className="text-center py-12">
                    <div
                      className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                    >
                      <CheckCircle size={28} className="text-white" />
                    </div>
                    <h3 className="font-sora font-bold text-xl text-white mb-3">
                      {lang === 'fr' ? 'Message envoyé !' : 'Message sent!'}
                    </h3>
                    <p className="text-gray-300 font-inter">{cTr.form.success}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h2 className="font-sora font-bold text-xl text-white mb-8 flex items-center gap-2">
                      <MessageSquare size={20} className="text-exevori-primary" />
                      {lang === 'fr' ? 'Envoyez-nous un message' : 'Send us a message'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div>
                        <label className="exevori-label">{cTr.form.name} *</label>
                        <input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          className="exevori-input"
                          data-testid="contact-name"
                          placeholder={lang === 'fr' ? 'Jean Tremblay' : 'John Smith'}
                        />
                      </div>
                      <div>
                        <label className="exevori-label">{cTr.form.company}</label>
                        <input
                          name="company"
                          value={form.company}
                          onChange={handleChange}
                          className="exevori-input"
                          data-testid="contact-company"
                          placeholder={lang === 'fr' ? 'Mon Entreprise Inc.' : 'My Company Inc.'}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div>
                        <label className="exevori-label">{cTr.form.email} *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          className="exevori-input"
                          data-testid="contact-email"
                          placeholder="jean@entreprise.ca"
                        />
                      </div>
                      <div>
                        <label className="exevori-label">{cTr.form.phone}</label>
                        <input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          className="exevori-input"
                          data-testid="contact-phone"
                          placeholder="581-700-4171"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div>
                        <label className="exevori-label">{cTr.form.projectType}</label>
                        <select
                          name="project_type"
                          value={form.project_type}
                          onChange={handleChange}
                          className="exevori-select"
                          data-testid="contact-project-type"
                        >
                          <option value="">— {lang === 'fr' ? 'Sélectionner' : 'Select'} —</option>
                          {cTr.form.projectTypes.map((pt, i) => (
                            <option key={i} value={pt}>{pt}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="exevori-label">{cTr.form.budget}</label>
                        <select
                          name="budget"
                          value={form.budget}
                          onChange={handleChange}
                          className="exevori-select"
                          data-testid="contact-budget"
                        >
                          <option value="">— {lang === 'fr' ? 'Sélectionner' : 'Select'} —</option>
                          {cTr.form.budgets.map((b, i) => (
                            <option key={i} value={b}>{b}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="exevori-label">{cTr.form.message} *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="exevori-input resize-none"
                        data-testid="contact-message"
                        placeholder={cTr.form.messagePlaceholder}
                      />
                    </div>
                    {status === 'error' && (
                      <p className="text-red-400 text-sm mb-4 font-inter">{cTr.form.error}</p>
                    )}
                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      data-testid="contact-submit"
                      className="btn-primary w-full justify-center text-base py-4"
                    >
                      {status === 'sending' ? cTr.form.sending : cTr.form.submit}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Info */}
            <div className={`lg:col-span-2 reveal-right ${visible ? 'visible' : ''}`}>
              <div className="exevori-card p-8 mb-6">
                <h3 className="font-sora font-bold text-white text-lg mb-6">{cTr.info.title}</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
                    >
                      <MapPin size={18} className="text-exevori-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-inter">
                        {lang === 'fr' ? 'Adresse' : 'Address'}
                      </div>
                      <div className="text-white font-inter">{cTr.info.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
                    >
                      <Phone size={18} className="text-exevori-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-inter">
                        {lang === 'fr' ? 'Téléphone' : 'Phone'}
                      </div>
                      <a
                        href="tel:+15817004171"
                        data-testid="contact-phone-link"
                        className="text-white font-inter hover:text-exevori-primary transition-colors"
                      >
                        {cTr.info.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
                    >
                      <Mail size={18} className="text-exevori-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-inter">
                        {lang === 'fr' ? 'Courriel' : 'Email'}
                      </div>
                      <a
                        href="mailto:contact@exevori.com"
                        data-testid="contact-email-link"
                        className="text-white font-inter hover:text-exevori-primary transition-colors"
                      >
                        {cTr.info.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
                    >
                      <Clock size={18} className="text-exevori-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-inter">
                        {lang === 'fr' ? 'Heures d\'ouverture' : 'Business Hours'}
                      </div>
                      <div className="text-white font-inter">{cTr.info.hours}</div>
                      <div className="text-gray-400 text-sm font-inter mt-1">{cTr.info.response}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Direct call CTA */}
              <a
                href="tel:+15817004171"
                data-testid="direct-call-btn"
                className="btn-primary w-full justify-center text-center block"
              >
                <Phone size={16} />
                {lang === 'fr' ? 'Appeler maintenant' : 'Call Now'}: (581) 700-4171
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
